<template>
  <top-navigation
    previous-route="Photos"
    :title="$t('components.comment.title')"
  />
  <div class="mx-auto min-h-screen pt-24 pb-12">
    <div
      v-if="photoDoc"
      class="sticky top-24 z-20 w-full origin-top transition transition-all duration-500"
    >
      <photo :simple="true" :photo-doc="photoDoc"></photo>
    </div>

    <InformationText
      v-if="fetchError"
      color="red"
      class="mx-2 mb-2"
      :information="fetchError"
    />

    <SkeletonLoader v-if="loading"></SkeletonLoader>

    <div v-if="photoDoc">
      <comment-component
        v-for="(comment, index) in comments"
        :key="`${comment.author}-${index}`"
        :author-uuid="comment.author"
        :previous-date="getPreviousDate(index)"
        :date="comment.created_at"
        :comment="comment.comment"
      />
    </div>
  </div>
  <div class="fixed bottom-0 left-0 z-20 flex w-full justify-center">
    <div class="w-full max-w-xl bg-white px-2 pb-4 dark:bg-slate-900">
      <comment-input
        :posting-comment="postingComment"
        @post-comment="postComment"
      ></comment-input>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, watch } from "vue";
import TopNavigation from "@/components/TopNavigation.vue";
import Photo from "@/views/Photo.vue";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import { usePhotosStore } from "@/stores/modules/photos/photos";
import router from "@/router/router";
import { logEvent } from "firebase/analytics";
import { firebaseAnalytics } from "@/helpers/firebase/firebase";
import CommentComponent from "@/components/Comment.vue";
import CommentInput from "@/components/CommentInput.vue";
import InformationText from "@/components/InformationText.vue";
import { useComments } from "@/composables/useComments";
import { photoCommentDocumentConverter } from "@kekkle/shared/convertors/web/comment-document.converter";
import type { PhotoDocument } from "@kekkle/shared/types/documents/photo-document/photo-document";
import SkeletonLoader from "../components/ui/SkeletonLoader.vue";

type Props = {
  photoId: string;
  groupId: string;
};

const props = defineProps<Props>();

const authStore = useAuthStore();
const groupsStore = useGroupsStore();
const userStore = useUserStore();
const photosStore = usePhotosStore();

// Get the photo from the store's allPhotos (populated by onSnapshot listener)
const photoDoc = computed(
  () =>
    photosStore.allPhotos.find((p: PhotoDocument) => p._id === props.photoId) ??
    null,
);

const {
  comments,
  posting: postingComment,
  fetchError,
  loading,
  postComment: postCommentToFirestore,
} = useComments({
  groupId: props.groupId,
  itemId: props.photoId,
  collectionName: "photos_comments",
  converter: photoCommentDocumentConverter,
  incrementField: "number_of_comments",
  stampUpdatedAt: true,
  authorUuid: authStore.authUuid ?? "",
});

boot();

watch(
  () => comments.value.length,
  async () => {
    setTimeout(() => {
      window.scrollTo({
        behavior: "smooth",
        left: 0,
        top: document.body.scrollHeight,
      });
    }, 40);
  },
  { immediate: true },
);

function getPreviousDate(index: number): Date | undefined {
  if (index === 0) return undefined;
  const previousComment = comments.value[index - 1];
  return previousComment?.created_at;
}

async function boot() {
  if (userStore.userData?.groups?.includes(props.groupId)) {
    if (userStore.activeGroup !== props.groupId) {
      if (!authStore.authUuid) {
        await router.push({ name: "Group" });
        return;
      }
      await userStore.switchActiveGroup(props.groupId);
      await groupsStore.refreshAllUserGroupsData();
    }
    // Ensure photo subscription is active for this group
    photosStore.subscribeToPhotos();
    window.scrollTo({
      behavior: "smooth",
      left: 0,
      top: document.body.scrollHeight,
    });
  } else {
    await router.push({ name: "Group" });
  }
}

async function postComment(e: { comment: string }) {
  logEvent(firebaseAnalytics, "added_comment", { for: "photo" });
  await postCommentToFirestore(e.comment);
}
</script>
